/**
 * Main player dashboard (requires GameSessionProvider from AuthLayout).
 * Composes score, fixture/result feed, league list, and country list cards plus league modals.
 * Opens the player survey modal on each visit until submitted (see docs/player-survey.md),
 * except admins with the localStorage retake toggle (`tt-survey-admin-retake`).
 * After survey dismiss/submit (or immediately if survey is not offered this visit), may open
 * SupportPlansModal once per page load unless supportPromptNever or hasSupported.
 * If member hydration stalls >15s, still render the profile shell so modals can open.
 */
import {
  Button,
  Center,
  Heading,
  Show,
  VStack,
  Flex,
  useBreakpointValue,
  useDisclosure,
  useToast,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { type FunctionsError, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { countryListValid, type Country } from '@/models/country';
import type { leagueCreationDetails } from '@/models/league';
import { sentinelType, type gameResult } from '@/models/gameUpdate';
import { doc, onSnapshot } from 'firebase/firestore';
import type { MatchdayConfig } from '@/models/scoring';
import { ACTIVE_TOURNAMENT, MATCHDAY_COUNT, SWAP_MATCHDAY } from '@/constants';
import { PROFILE_SCROLL_COLUMN_MIN_H, VIEWPORT_MIN_H } from '@/constants/mobileLayout';
import { AuthedPageContainer } from '@/components';
import { FirebaseContext } from '@/session/FirebaseContext';
import { CreateLeagueModal, PlayerSurveyModal, StringEntryModal, SupportPlansModal } from '@/components/modals';
import { createLeagueToastOptions, firebaseErrorToastOptions } from '../../helpers';
import {
  hasSettledSupportPromptThisLoad,
  markSupportPromptSettledThisLoad,
} from '@/helpers/auth/supportPromptGate';
import {
  getAdminSurveyRetakeEnabled,
  loadPlayerSurveyResponseAnswers,
  type PlayerSurveyAnswers,
} from '@/models/playerSurvey';
import { setSupportPromptNever, updateTeamSelectionData, updateUser } from '@/models/user';
import { useGameSession } from '@/session/useGameSession';
import { useUserProfile } from '@/session/useUserProfile';
import { LiveMatchesList } from '@/components/LiveMatchesList';
import { useLiveMatches } from '@/hooks/useLiveMatches';
import { dedupeGameResults } from '@/helpers/roster/dedupeGameResults';
import { MyListScoreHelpButton } from '@/components/MyListScoreHelpButton';
import { ScoreCard } from './ScoreCard';
import { PointsUpdatingCard } from './PointsUpdatingCard';
import { LeagueListCard } from './LeagueListCard';
import { CountryListCard } from './CountryListCard';
import { ProfileMatchFeedTabs } from './ProfileMatchFeedTabs';
import { Card } from './Card';

/** After this, render profile shell + modals even if league member snapshots are still loading. */
const SESSION_LOAD_TIMEOUT_MS = 15_000;

export default function ProfilePage() {
  const { fixtures, gameUpdates, leagues, memberData, membersHydrated, sessionPhase } = useGameSession();
  const { userData, teamSelectionData } = useUserProfile();
  const [sessionLoadTimedOut, setSessionLoadTimedOut] = useState(false);
  const pointsUpdating = Object.keys(gameUpdates).includes(sentinelType.pointsUpdating);

  useEffect(() => {
    if (membersHydrated || sessionPhase === 'ready' || sessionPhase === 'error') {
      setSessionLoadTimedOut(false);
      return;
    }
    const id = setTimeout(() => setSessionLoadTimedOut(true), SESSION_LOAD_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [membersHydrated, sessionPhase]);

  const currentTournamentSelection = teamSelectionData[ACTIVE_TOURNAMENT] ?? {};
  const {
    countryList = [],
    listIndexSwapPair = null,
    currentScore = 0,
    prevScore = 0,
    listVisibleInLeagues = false,
    mdReset = 0,
  } = currentTournamentSelection;

  const ctx = useContext(FirebaseContext);
  const navigate = useNavigate();

  const createLeagueDisclosure = useDisclosure();
  const joinLeagueDisclosure = useDisclosure();
  const { isOpen: surveyIsOpen, onOpen: openSurvey, onClose: closeSurvey } = useDisclosure();
  const {
    isOpen: supportPromptIsOpen,
    onOpen: openSupportPrompt,
    onClose: closeSupportPrompt,
  } = useDisclosure();
  const surveyOpenedThisVisitRef = useRef(false);
  const toast = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRetake, setAdminRetake] = useState(false);
  const [savedSurveyAnswers, setSavedSurveyAnswers] = useState<PlayerSurveyAnswers | undefined>();
  const [savedAnswersReady, setSavedAnswersReady] = useState(false);
  const [supportNeverBusy, setSupportNeverBusy] = useState(false);

  useEffect(() => {
    if (!ctx.user) {
      setIsAdmin(false);
      return;
    }
    void ctx.user.getIdTokenResult().then((token) => {
      setIsAdmin(token.claims.admin === true);
    });
  }, [ctx.user]);

  useEffect(() => {
    if (!ctx.user) {
      setAdminRetake(false);
      return;
    }
    const uid = ctx.user.uid;
    const sync = () => setAdminRetake(getAdminSurveyRetakeEnabled(uid));
    sync();
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key.includes('tt-survey-admin-retake')) sync();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [ctx.user]);

  const surveyRetakeActive = isAdmin && adminRetake;
  const retakeNeedsSavedAnswers = surveyRetakeActive && !!userData?.playerSurveyCompleted;
  const shouldOfferSurvey =
    !!userData && (!userData.playerSurveyCompleted || surveyRetakeActive);

  function maybeOpenSupportPrompt() {
    if (!userData || userData.supportPromptNever || userData.hasSupported) return;
    if (hasSettledSupportPromptThisLoad()) return;
    // Do not mark here — only after the modal mounts open (onPresented) or the user dismisses.
    // Session league hydration can unmount Profile before paint; marking on attempt would
    // permanently suppress the CTA for the rest of the page load.
    openSupportPrompt();
  }

  function handleSurveyClosed() {
    closeSurvey();
    // Do not wait for surveyIsOpen to flip — open Support CTA immediately after dismiss/submit.
    maybeOpenSupportPrompt();
  }

  const handleSupportPromptPresented = useCallback(() => {
    markSupportPromptSettledThisLoad();
  }, []);

  function handleSupportPromptNotNow() {
    markSupportPromptSettledThisLoad();
    closeSupportPrompt();
  }

  function handleSupportPromptSupport() {
    markSupportPromptSettledThisLoad();
    closeSupportPrompt();
    navigate('/support');
  }

  async function handleSupportPromptNever() {
    if (!ctx.user || supportNeverBusy) return;
    setSupportNeverBusy(true);
    try {
      await setSupportPromptNever(ctx.db, ctx.user.uid);
      markSupportPromptSettledThisLoad();
      closeSupportPrompt();
    } catch (err) {
      console.error('[ProfilePage] setSupportPromptNever', err);
      toast({
        title: 'Could not save preference',
        description: 'Please try again in a moment.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSupportNeverBusy(false);
    }
  }

  useEffect(() => {
    if (!ctx.user || !retakeNeedsSavedAnswers) {
      setSavedSurveyAnswers(undefined);
      setSavedAnswersReady(!retakeNeedsSavedAnswers);
      return;
    }
    setSavedAnswersReady(false);
    let cancelled = false;
    void loadPlayerSurveyResponseAnswers(ctx.db, ctx.user.uid).then((answers) => {
      if (cancelled) return;
      setSavedSurveyAnswers(answers ?? {});
      setSavedAnswersReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.db, ctx.user, retakeNeedsSavedAnswers]);

  useEffect(() => {
    if (!shouldOfferSurvey || surveyOpenedThisVisitRef.current) return;
    if (retakeNeedsSavedAnswers && !savedAnswersReady) return;
    surveyOpenedThisVisitRef.current = true;
    openSurvey();
  }, [shouldOfferSurvey, openSurvey, retakeNeedsSavedAnswers, savedAnswersReady]);

  useEffect(() => {
    if (!userData || shouldOfferSurvey) return;
    maybeOpenSupportPrompt();
    // Open once when survey is not offered; module gate prevents repeats this page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot after profile ready
  }, [userData, shouldOfferSurvey]);

  const cardMaxW = useBreakpointValue({ base: '100%' });
  const cardPadX = useBreakpointValue({ base: 3, md: 5 });
  const resultPadX = cardPadX;
  /** Narrower than full PageContainer — more side margin than before, not as wide as home hero. */
  const profileBandMaxW = 'min(1220px, 100%)';
  const profileBandPx = useBreakpointValue({ base: 4, md: 8, xl: 12 });
  const mobileStackSpacing = useBreakpointValue({ base: 5, md: 6 });
  const mobileCardRadius = 16;
  const mobileFixturesMaxW = '100%';

  const mobileSectionCardProps = {
    w: 'full' as const,
    maxW: cardMaxW,
    borderRadius: mobileCardRadius,
    px: cardPadX,
    height: 'unset' as const,
  };

  const leagueKeys = Object.keys(leagues);
  const isInGlobalLeague = leagueKeys.includes('global');

  const [pendingList, setPendingList] = useState<Country[]>();
  const [pendingLeagueName, setPendingLeagueName] = useState<string>();
  const [matchdayConfig, setMatchdayConfig] = useState<MatchdayConfig>();
  const [listVisibilityLoading, setListVisibilityLoading] = useState(false);

  // --- Pending list & league localStorage (post-signup recovery) ---

  useEffect(() => {
    return onSnapshot(doc(ctx.db, 'competitions', ACTIVE_TOURNAMENT), (snapshot) => {
      setMatchdayConfig(snapshot.exists() ? (snapshot.data() as MatchdayConfig) : undefined);
    });
  }, [ctx.db]);

  useEffect(() => {
    setPendingLeagueName(localStorage.getItem('pendingLeagueName') ?? undefined);
    const pendingListString = localStorage.getItem('pendingList');
    if (pendingListString) {
      try {
        const countryList = pendingListString.split(',') as Country[];
        setPendingList(countryList);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    if (!pendingList || !ctx.user) return;
    if (countryListValid(pendingList)) {
      updateTeamSelectionData(ctx.db, ctx.user!.uid, {
        countryList: pendingList,
        spectator: false,
      }).then(() => {
        localStorage.removeItem('pendingList');
        localStorage.removeItem('pendingGoalsGuess');
        setPendingList(undefined);
      });
      updateUser(ctx.db, ctx.user!.uid, {
        requiresShareUpdate: true,
      });
    } else {
      localStorage.removeItem('pendingList');
      localStorage.removeItem('pendingGoalsGuess');
      setPendingList(undefined);
    }
  }, [pendingList, ctx.db, ctx.user]);

  useEffect(() => {
    if (!!pendingLeagueName && pendingLeagueName.trim().length > 0) {
      const cloudFunc = httpsCallable(ctx.funcs, 'createLeague');
      try {
        const details: leagueCreationDetails = { name: pendingLeagueName };
        cloudFunc(details).then((result) => {
          const uid = (result.data as { uid: string }).uid;
          toast(createLeagueToastOptions(details));
          navigate(`../league/${uid}`);
        });
      } catch (error) {
        toast(firebaseErrorToastOptions('Failed To Create League', error as FunctionsError));
      }
    }
  }, [pendingLeagueName]);

  const hasCompleteList = countryList.length === 10;
  const liveMatches = useLiveMatches();

  // --- List visibility (setListVisibleInLeagues callable) ---

  async function handleListVisibilityChange(visible: boolean) {
    setListVisibilityLoading(true);
    try {
      await httpsCallable(ctx.funcs, 'setListVisibleInLeagues')({ visible });
    } catch (error) {
      toast(firebaseErrorToastOptions('Could not update list sharing', error as FunctionsError));
    } finally {
      setListVisibilityLoading(false);
    }
  }

  // Always mount so onPresented can settle the page-load gate even across brief UI gates.
  const supportPlansModal = (
    <SupportPlansModal
      isOpen={supportPromptIsOpen}
      isNeverBusy={supportNeverBusy}
      onPresented={handleSupportPromptPresented}
      onSupport={handleSupportPromptSupport}
      onNotNow={handleSupportPromptNotNow}
      onNever={() => void handleSupportPromptNever()}
    />
  );

  // --- Session loading / error gates ---

  if (sessionPhase === 'error' || sessionLoadTimedOut) {
    return (
      <>
        <Center w='full' minH={VIEWPORT_MIN_H} px={4}>
          <VStack spacing={4}>
            <Heading fontSize={24} textAlign='center'>
              Could not load your game
            </Heading>
            <Button colorScheme='green' onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          </VStack>
        </Center>
        {supportPlansModal}
      </>
    );
  }

  if (membersHydrated) {
    if (!!pendingList) {
      return (
        <>
          <Center w='full' minH={VIEWPORT_MIN_H}>
            <VStack spacing={4}>
              <Heading fontSize={24}>Creating your list.</Heading>
              <Spinner />
            </VStack>
          </Center>
          {supportPlansModal}
        </>
      );
    } else {
      if (gameUpdates) {
        const tournamentStarted = Object.keys(gameUpdates).includes(sentinelType.tournamentStarted);
        const currentMatchday = matchdayConfig?.currentMatchday ?? 0;

        const gameResults = dedupeGameResults(
          Object.keys(gameUpdates)
            .filter((x) => !(x in sentinelType) && gameUpdates[x].timestamp !== null)
            .map((x) => ({ docId: x, result: gameUpdates[x] as gameResult })),
        ).results;

        const countryListToDisplay = [...countryList];
        const fixtureListToDisplay = [...fixtures];
        const swapPair = listIndexSwapPair;
        if (swapPair) {
          const temp = countryListToDisplay[swapPair[0]];
          countryListToDisplay[swapPair[0]] = countryListToDisplay[swapPair[1]];
          countryListToDisplay[swapPair[1]] = temp;
        }

        const myListScoreHelpProps = {
          tournamentStarted,
          countryList,
          swapPair: listIndexSwapPair,
          mdReset,
          gameResults,
          currentMatchday,
          matchdayCount: matchdayConfig?.matchdayCount ?? MATCHDAY_COUNT,
          swapMatchday: matchdayConfig?.swapMatchday ?? SWAP_MATCHDAY,
        };

        // --- Card layout (desktop + mobile) and modals ---

        return (
          <AuthedPageContainer userData={userData}>
            <Show above='xl'>
              <Box maxW={profileBandMaxW} mx='auto' px={profileBandPx} pt={4} pb={5}>
                <Flex
                  direction='row'
                  align='stretch'
                  h={PROFILE_SCROLL_COLUMN_MIN_H}
                  minH={0}
                  gap={4}
                  width='full'
                >
                  <Flex direction='column' flex='3 1 0' minW={0} minH={0} h='full'>
                    <CountryListCard
                      countryList={countryListToDisplay}
                      headerAction={<MyListScoreHelpButton {...myListScoreHelpProps} />}
                      maxW={cardMaxW}
                      flex={1}
                      minH={0}
                      h='full'
                    />
                  </Flex>
                  <Flex direction='column' flex='3 1 0' minW={0} minH={0} h='full'>
                    <LeagueListCard
                      leagues={leagues}
                      memberData={memberData}
                      isInGlobalLeague={isInGlobalLeague}
                      maxW={cardMaxW}
                      px={cardPadX}
                      flex={1}
                      minH={0}
                      h='full'
                      onCreateLeague={() => createLeagueDisclosure.onOpen()}
                      onJoinLeague={() => joinLeagueDisclosure.onOpen()}
                      hasCompleteList={hasCompleteList}
                      listVisibleInLeagues={!!listVisibleInLeagues}
                      listVisibilityLoading={listVisibilityLoading}
                      onListVisibilityChange={(visible) => void handleListVisibilityChange(visible)}
                    />
                  </Flex>
                  <Flex direction='column' flex='4 1 0' minW={0} minH={0} h='full' gap={3}>
                    {tournamentStarted ? (
                      pointsUpdating ? (
                        <PointsUpdatingCard compact w='full' />
                      ) : (
                        <ScoreCard
                          currentScore={currentScore}
                          prevScore={prevScore}
                          showScoringHelp
                          compact
                          w='full'
                        />
                      )
                    ) : null}
                    <ProfileMatchFeedTabs
                      embedded
                      singleLineTeamNames
                      userData={userData}
                      gameResults={gameResults}
                      currentMatchday={currentMatchday}
                      fixtures={fixtureListToDisplay}
                      liveMatches={liveMatches}
                      resultPadX={resultPadX}
                      flex={1}
                      minH={0}
                      minW={0}
                      w='full'
                    />
                  </Flex>
                </Flex>
              </Box>
            </Show>
            <Show below='xl'>
              <VStack
                maxW={profileBandMaxW}
                mx='auto'
                px={profileBandPx}
                pt={4}
                pb={5}
                spacing={mobileStackSpacing}
                width='full'
                align='stretch'
                minW={0}
              >
                {liveMatches.length > 0 ? (
                  <Card {...mobileSectionCardProps}>
                    <LiveMatchesList liveMatches={liveMatches} svg showHeading />
                  </Card>
                ) : null}
                <CountryListCard
                  countryList={countryListToDisplay}
                  headerAction={<MyListScoreHelpButton {...myListScoreHelpProps} />}
                  {...mobileSectionCardProps}
                  scrollContained={false}
                />
                {tournamentStarted ? (
                  pointsUpdating ? (
                    <PointsUpdatingCard {...mobileSectionCardProps} />
                  ) : (
                    <ScoreCard
                      currentScore={currentScore}
                      prevScore={prevScore}
                      showScoringHelp
                      {...mobileSectionCardProps}
                    />
                  )
                ) : null}
                <LeagueListCard
                  leagues={leagues}
                  memberData={memberData}
                  isInGlobalLeague={isInGlobalLeague}
                  {...mobileSectionCardProps}
                  scrollContained={false}
                  onCreateLeague={() => createLeagueDisclosure.onOpen()}
                  onJoinLeague={() => joinLeagueDisclosure.onOpen()}
                  hasCompleteList={hasCompleteList}
                  listVisibleInLeagues={!!listVisibleInLeagues}
                  listVisibilityLoading={listVisibilityLoading}
                  onListVisibilityChange={(visible) => void handleListVisibilityChange(visible)}
                />
                <ProfileMatchFeedTabs
                  userData={userData}
                  gameResults={gameResults}
                  currentMatchday={currentMatchday}
                  fixtures={fixtureListToDisplay}
                  liveMatches={liveMatches}
                  maxW={mobileFixturesMaxW}
                  resultPadX={resultPadX}
                  w='full'
                />
              </VStack>
            </Show>

            <CreateLeagueModal isOpen={createLeagueDisclosure.isOpen} onClose={createLeagueDisclosure.onClose} />

            <StringEntryModal
              isOpen={joinLeagueDisclosure.isOpen}
              title='Join League'
              label='6 Digit Authentication Code'
              onClose={joinLeagueDisclosure.onClose}
              onSubmit={(authCode) => navigate(`../join/${authCode}`)}
              isValid={(authCode) => authCode.trim().length === 6}
              helperText='Anyone in your league can give this to you.'
              converter={(x) => x.toUpperCase()}
            />

            {ctx.user && shouldOfferSurvey ? (
              <PlayerSurveyModal
                isOpen={surveyIsOpen}
                onClose={handleSurveyClosed}
                uid={ctx.user.uid}
                retake={surveyRetakeActive && !!userData?.playerSurveyCompleted}
                savedAnswers={savedSurveyAnswers}
              />
            ) : null}

            {supportPlansModal}
          </AuthedPageContainer>
        );
      }
    }
  }
  return (
    <>
      <Center w='full' minH={VIEWPORT_MIN_H}>
        <Spinner />
      </Center>
      {supportPlansModal}
    </>
  );
}
